import React, {FC, ReactNode} from 'react';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {Box, styled, Typography} from '@mui/material';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {Image} from '@jobseeker/lotus/components/Image';
import {CrossOutlined} from '@jobseeker/lotus/icons';
import dynamic from 'next/dynamic';

const BottomSheet = dynamic(
  () => import('@jobseeker/lotus/elements/BottomSheet'),
  {ssr: false}
);

interface CustomDialogProps {
  open: boolean;
  title: string | any;
  subTitle?: ReactNode;
  Content?: ReactNode;
  children?: ReactNode;
  onClose: () => void;
  useBottomSheet?: boolean;
  fullScreen?: boolean;
  alert?: boolean;
  sx?: any;
}

const CustomBottomSheet = styled(BottomSheet)(() => ({
  zIndex: 1000,
  position: 'fixed',
  bottom: '0',
  left: '0',
  right: '0',
  padding: '20px',
  background: '#CCC',
}));

const CustomDialog: React.FC<CustomDialogProps> = ({
  open,
  title,
  subTitle,
  Content,
  onClose,
  children,
  useBottomSheet = true,
  fullScreen = false,
  sx,
  ...props
}) => {
  const isLarge = useIsLargeDevice();

  if (!isLarge && useBottomSheet) {
    return (
      <CustomBottomSheet
        header={
          <Box p={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                cursor: 'default',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Typography variant="subtitle1">{title}</Typography>
              <Image
                src={CrossOutlined}
                alt={'Close Icon'}
                width={18}
                height={18}
                onClick={onClose}
                objectFit="cover"
                style={{cursor: 'pointer'}}
              />
            </Box>
          </Box>
        }
        onDismiss={onClose}
        open={open}
        bannerMode={false}
        snapPoints={({minHeight, maxHeight}) => [
          maxHeight < minHeight ? maxHeight : minHeight,
        ]}
        {...props}
      >
        <Box p={2}>
          {subTitle &&
            (typeof subTitle === 'string' ? (
              <Typography
                variant="subtitle2"
                lineHeight="20px"
                color="text.secondary"
              >
                {subTitle}
              </Typography>
            ) : (
              subTitle
            ))}
          {Content && Content}
          {children}
        </Box>
      </CustomBottomSheet>
    );
  } else {
    return (
      <Dialog
        title={title}
        variant={VersionVariants.VERSION_THREE}
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        sx={{
          '& .MuiDialog-paper': {
            minWidth: {xxs: 'calc(100vw - 16px)', md: '500px'},
            maxWidth: {xxs: 'calc(100vw - 16px)', md: '520px'},
          },
          ...sx,
        }}
        disableCrossButton={props.alert}
        titleProps={{
          pt: 3,
          px: {xxs: 2, md: 3},
        }}
        {...props}
      >
        <Box px={{xxs: 1, md: 2}}>
          {subTitle &&
            (typeof subTitle === 'string' ? (
              <Typography
                lineHeight="20px"
                variant="subtitle2"
                color="text.secondary"
              >
                {subTitle}
              </Typography>
            ) : (
              subTitle
            ))}
          {Content && Content}
          {children}
        </Box>
      </Dialog>
    );
  }
};

export default CustomDialog;
